/**
 * CAOS-346 — the announcement that something is now OPEN in the workspace, so the chrome can keep it
 * in the open-records strip. A DOM event rather than a direct call: a content surface must not reach
 * into the frame that hosts it, and the shell is free to ignore it (a pure/test render simply has no
 * listener).
 *
 * CAOS-695 — a record is not the only thing that opens. A Setup page announces itself the same way,
 * so it earns a subtab beside Setup's Home exactly as a record earns one beside its list. Two things
 * a Setup page needs that a record does not:
 *   • `id` — the ONE entry the announcement belongs to. A section is one subtab however deep inside
 *     it you go, so `/setup/roles` and `/setup/roles/sales_rep` share the id `/setup/roles` while the
 *     entry's `href` follows you. Absent ⇒ the href is the id, which is what a record has always been.
 *   • `closeTo` — where closing the entry you are looking at sends you. Absent ⇒ the list the record
 *     came from, as before.
 */
export const OPEN_RECORD_EVENT = 'caos-record-opened';

export interface OpenRecordAnnouncement {
  readonly href: string;
  readonly label: string;
  readonly id?: string;
  readonly closeTo?: string;
}

/**
 * The most recent announcement. The page a full document load opens on is resolved BEFORE the frame
 * is assembled (the frame is handed the resolved content), so its announcement is dispatched while
 * nothing is listening yet; the frame reads this back once it is built. See `assembleShellFrame`.
 */
let lastAnnouncement: OpenRecordAnnouncement | undefined;

/** Announce an opened record or page to whatever chrome is listening. */
export function announceOpenRecord(detail: OpenRecordAnnouncement): void {
  lastAnnouncement = detail;
  if (typeof document === 'undefined') return;
  document.dispatchEvent(new CustomEvent(OPEN_RECORD_EVENT, { detail }));
}

/** The last announcement made, whether or not anything was listening for it. */
export function lastOpenRecordAnnouncement(): OpenRecordAnnouncement | undefined {
  return lastAnnouncement;
}

/**
 * Forget the last announcement, as a window that has opened nothing. A suite that assembles several
 * frames calls this between cases, so no case takes up a page another one opened.
 */
export function resetOpenRecordAnnouncement(): void {
  lastAnnouncement = undefined;
}
